import { ICustomFieldResSchema } from '@monetize/types/app';
import { FC } from 'react';
import { MdInfo } from 'react-icons/md';
import {
  MBox,
  MFlex,
  MIcon,
  MText,
  MTooltip,
} from '../../../../components/Monetize';

interface UsagePropertyColumnHeaderProps {
  propertyKey: string;
  /** Configured metadata for this key, when the tenant has any. */
  field?: ICustomFieldResSchema;
}

/**
 * Header for a `properties.<key>` usage table column. A configured field shows
 * its label with the API name underneath (the CSV column is `properties.<key>`,
 * so users need both) and an info icon carrying the field's tooltip. A key that
 * only exists in the data falls back to the bare key.
 */
export const UsagePropertyColumnHeader: FC<UsagePropertyColumnHeaderProps> = ({
  propertyKey,
  field,
}) => {
  if (!field) {
    return <MText fontWeight="semibold">{propertyKey}</MText>;
  }
  const tooltip = field.tooltip || field.description;
  return (
    <MFlex direction="column" lineHeight="short" minW={0}>
      <MFlex alignItems="center" gap={1} minW={0}>
        <MText fontWeight="semibold" lineClamp={1}>
          {field.displayLabel}
        </MText>
        {tooltip && (
          <MTooltip label={tooltip} hasArrow>
            <MBox
              as="span"
              display="inline-flex"
              alignItems="center"
              role="img"
              tabIndex={0}
              aria-label={tooltip}
              data-testid={`usage-property-tooltip-${propertyKey}`}
            >
              <MIcon as={MdInfo} boxSize={3} color="fg.muted" />
            </MBox>
          </MTooltip>
        )}
      </MFlex>
      <MText fontSize="xs" fontWeight="normal" color="fg.muted" lineClamp={1}>
        {propertyKey}
      </MText>
    </MFlex>
  );
};
